import { useState, useEffect } from 'react';
import Spinner from '../Spinner/Spinner';
import './ProfileForm.css';

function ProfileForm({ initialData, onSave, compact = false, submitting = false }) {
    const [givenName, setGivenName] = useState('');
    const [familyName, setFamilyName] = useState('');
    const [birthDate, setBirthDate] = useState('');
    const [sex, setSex] = useState('');
    const [menopausal, setMenopausal] = useState(false);
    const [race, setRace] = useState('');
    const [errors, setErrors] = useState([]);

    useEffect(() => {
        if (initialData) {
            if (initialData.givenName) setGivenName(initialData.givenName);
            if (initialData.familyName) setFamilyName(initialData.familyName);
            if (initialData.birthDate) setBirthDate(initialData.birthDate);
            if (initialData.sex) setSex(initialData.sex);
            if (initialData.menopausal !== undefined) setMenopausal(initialData.menopausal);
            if (initialData.race) setRace(initialData.race);
        }
    }, [initialData]);

    const NAME_REGEX = /^[\p{L}\p{M}' -]+$/u;
    const MAX_NAME_LENGTH = 100;

    const handleSubmit = (e) => {
        e.preventDefault();

        const missing = [];
        if (!givenName.trim()) missing.push('First Name');
        if (!familyName.trim()) missing.push('Last Name');
        if (!birthDate) missing.push('Birth Date');
        if (!sex) missing.push('Sex');
        if (!race) missing.push('Race');

        if (givenName.trim() && (givenName.length > MAX_NAME_LENGTH || !NAME_REGEX.test(givenName.trim()))) {
            missing.push('First Name (letters, hyphens, apostrophes only, max 100 chars)');
        }
        if (familyName.trim() && (familyName.length > MAX_NAME_LENGTH || !NAME_REGEX.test(familyName.trim()))) {
            missing.push('Last Name (letters, hyphens, apostrophes only, max 100 chars)');
        }

        if (missing.length > 0) {
            setErrors(missing);
            return;
        }

        setErrors([]);
        onSave({
            givenName,
            familyName,
            birthDate,
            sex,
            menopausal: sex === 'female' ? menopausal : null,
            race,
        });
    };

    const clearError = (field) => {
        setErrors(prev => prev.filter(err => err !== field));
    };

    return (
        <form className={`profile-form ${compact ? 'profile-form--compact' : ''}`} onSubmit={handleSubmit}>
            {errors.length > 0 && (
                <div className="profile-form-error">
                    These fields are required: {errors.join(', ')}
                </div>
            )}

            <div className="form-group">
                <label htmlFor="pf-givenName">First Name</label>
                <input
                    id="pf-givenName"
                    type="text"
                    value={givenName}
                    maxLength={100}
                    onChange={(e) => { setGivenName(e.target.value); if (e.target.value) clearError('First Name'); }}
                    className={errors.includes('First Name') ? 'input-error' : ''}
                />
            </div>

            <div className="form-group">
                <label htmlFor="pf-familyName">Last Name</label>
                <input
                    id="pf-familyName"
                    type="text"
                    value={familyName}
                    maxLength={100}
                    onChange={(e) => { setFamilyName(e.target.value); if (e.target.value) clearError('Last Name'); }}
                    className={errors.includes('Last Name') ? 'input-error' : ''}
                />
            </div>

            <div className="profile-form-inline">
                <div className="form-group">
                    <label htmlFor="pf-birthDate">Birth Date</label>
                    <input
                        id="pf-birthDate"
                        type="date"
                        value={birthDate}
                        onChange={(e) => { setBirthDate(e.target.value); if (e.target.value) clearError('Birth Date'); }}
                        className={errors.includes('Birth Date') ? 'input-error' : ''}
                    />
                </div>
                <div className="form-group">
                    <label htmlFor="pf-sex">Sex</label>
                    <select
                        id="pf-sex"
                        value={sex}
                        onChange={(e) => {
                            setSex(e.target.value);
                            if (e.target.value) clearError('Sex');
                            if (e.target.value !== 'female') setMenopausal(false);
                        }}
                        className={errors.includes('Sex') ? 'input-error' : ''}
                    >
                        <option value="">Select Sex</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                    </select>
                </div>
            </div>

            {sex === 'female' && (
                <div className="profile-form-checkbox">
                    <label htmlFor="pf-menopausal">Post Menopause</label>
                    <input
                        id="pf-menopausal"
                        type="checkbox"
                        checked={!!menopausal}
                        onChange={(e) => setMenopausal(e.target.checked)}
                    />
                </div>
            )}

            <div className="form-group">
                <label htmlFor="pf-race">Race</label>
                <select
                    id="pf-race"
                    value={race}
                    onChange={(e) => { setRace(e.target.value); if (e.target.value) clearError('Race'); }}
                    className={errors.includes('Race') ? 'input-error' : ''}
                >
                    <option value="">Select Race</option>
                    <option value="white">White</option>
                    <option value="black">Black or African American</option>
                    <option value="asian">Asian</option>
                    <option value="hispanic">Hispanic/Native American</option>
                    <option value="other">Other</option>
                </select>
            </div>

            <button
                className="profile-form-submit"
                type="submit"
                disabled={submitting}
            >
                {submitting ? <Spinner size={20} color="#fff" /> : 'Save'}
            </button>
        </form>
    );
}

export default ProfileForm;
